import { useMutation } from "@apollo/client";
import TurnstileWidget from "components/common/TurnstileWidget";
import trackEvent from "components/tracking/trackEvent";
import { SUBMIT_CREATOR_APPLICATION } from "graphql/mutations/creatorApplication";
import { FormEvent, useState } from "react";
import styles from "../CreatorsPage.module.scss";

// The server accepts exactly these (graphql/schemas/creatorApplication.ts), and caps each answer at the maxLength below.
const FOLLOWER_SIZES = ["Under 10K", "10K–50K", "50K–250K", "250K+", "I have a blog"];

/** "Tell us about you": files the application in Stippl's Creators list, behind the same Turnstile check as the hotel inquiry. */
const ApplyForm = () => {
	const [submitApplication, { loading }] = useMutation(SUBMIT_CREATOR_APPLICATION);
	const [captchaToken, setCaptchaToken] = useState<string | null>(null);
	// Bumped after a failed attempt: a Turnstile token is single-use, so the retry needs a fresh widget.
	const [widgetKey, setWidgetKey] = useState(0);
	const [done, setDone] = useState(false);
	const [failed, setFailed] = useState(false);
	const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

	const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const form = new FormData(e.currentTarget);
		const answer = (name: string) => String(form.get(name) ?? "");
		const size = answer("size");
		setFailed(false);
		try {
			await submitApplication({
				variables: {
					input: { name: answer("name"), email: answer("email"), handle: answer("handle"), size, dest: answer("dest"), why: answer("why") },
					captchaToken,
				},
			});
		} catch (error: any) {
			console.error("[CreatorApplication] Submit failed:", { error: error?.message || error });
			setFailed(true);
			setCaptchaToken(null);
			setWidgetKey((key) => key + 1);
			return;
		}
		setDone(true);
		try {
			trackEvent("creator_application_submitted", { size }, "marketing_site");
		} catch {
			// tracking must never affect a submitted application
		}
	};

	return (
		<form className={styles.form} onSubmit={onSubmit}>
			<label htmlFor="f-name">
				Your name
				<input id="f-name" name="name" autoComplete="name" required maxLength={100} />
			</label>
			<label htmlFor="f-email">
				Email
				<input id="f-email" name="email" type="email" autoComplete="email" required maxLength={254} />
			</label>
			<label htmlFor="f-handle">
				Instagram or TikTok handle
				<input id="f-handle" name="handle" placeholder="@yourname" maxLength={100} />
			</label>
			<label htmlFor="f-size">
				Followers
				<select id="f-size" name="size" defaultValue="10K–50K">
					{FOLLOWER_SIZES.map((size) => (
						<option key={size}>{size}</option>
					))}
				</select>
			</label>
			<label className={styles.full} htmlFor="f-dest">
				Which places do you know best?
				<input id="f-dest" name="dest" placeholder="Bangkok, the Amalfi Coast, Hokkaido…" maxLength={300} />
			</label>
			<label className={styles.full} htmlFor="f-why">
				Anything else we should know?
				<textarea id="f-why" name="why" maxLength={2000} />
			</label>
			{turnstileSiteKey && (
				<div className={styles.full}>
					<TurnstileWidget key={widgetKey} siteKey={turnstileSiteKey} onVerify={setCaptchaToken} onExpire={() => setCaptchaToken(null)} />
				</div>
			)}
			<div className={styles.full}>
				<button className={`${styles.btn} ${styles.btnGreen}`} type="submit" disabled={loading || done || (!!turnstileSiteKey && !captchaToken)}>
					Send application
				</button>
			</div>
			<p className={styles.done} hidden={!done}>
				Thanks! We read every application and get back to you within a week.
			</p>
			{failed && (
				<p className={`${styles.done} ${styles.failed}`} role="alert">
					Something went wrong. Please try again.
				</p>
			)}
		</form>
	);
};

export default ApplyForm;
